import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import type { UtilisationGranularity, UtilisationMode } from '@timelog/pm-shared';
import { useTranslation } from 'react-i18next';

import { HEAT_LEGEND_STEPS } from './heat';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/** The value-cell metric: utilisation percentages or plain hours. */
export type PlannerMetric = 'pct' | 'hours';

export const isPlannerMetric = (value: unknown): value is PlannerMetric =>
  value === 'pct' || value === 'hours';

type SegmentOption<T extends string> = { value: T; label: string; title?: string };

/** The design system's sunken-track segmented control (PlanViewSwitcher). */
const Segment = <T extends string>({
  value,
  options,
  onChange,
  automationId,
}: {
  value: T;
  options: SegmentOption<T>[];
  onChange: (next: T) => void;
  automationId: string;
}) => (
  <Box
    data-automation-id={automationId}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '2px',
      p: '2px',
      borderRadius: 'var(--border-radius-pill)',
      bgcolor: 'var(--bg-sunken)',
    }}
  >
    {options.map((option) => {
      const selected = value === option.value;
      return (
        <Button
          key={option.value}
          size="small"
          variant="text"
          color="secondary"
          aria-pressed={selected}
          title={option.title}
          onClick={() => onChange(option.value)}
          data-automation-id={`${automationId}-${option.value}`}
          data-selected={selected}
          sx={
            selected
              ? {
                  bgcolor: 'var(--bg-panel)',
                  color: 'var(--fg-1)',
                  '&:hover': { bgcolor: 'var(--bg-panel)' },
                }
              : { color: 'var(--fg-3)' }
          }
        >
          {option.label}
        </Button>
      );
    })}
  </Box>
);

export type PlannerToolbarProps = {
  mode: UtilisationMode;
  onMode: (next: UtilisationMode) => void;
  granularity: UtilisationGranularity;
  onGranularity: (next: UtilisationGranularity) => void;
  metric: PlannerMetric;
  onMetric: (next: PlannerMetric) => void;
  overCapacityCells: number;
};

/** The planner's view controls: mode, scale, metric, heat legend, over chip. */
export const PlannerToolbar = ({
  mode,
  onMode,
  granularity,
  onGranularity,
  metric,
  onMetric,
  overCapacityCells,
}: PlannerToolbarProps) => {
  const { t } = useTranslation(['pmResources', 'pmCommon']);
  return (
    <Stack
      direction="row"
      data-automation-id="planner-toolbar"
      sx={{ alignItems: 'center', gap: 2, flexWrap: 'wrap', px: 2, pb: 1.5 }}
    >
      <Segment
        automationId="planner-mode"
        value={mode}
        onChange={onMode}
        options={[
          { value: 'raw', label: t('toolbar.ModeRaw') },
          {
            value: 'weighted',
            label: t('toolbar.ModeWeighted'),
            title: t('toolbar.ModeWeightedTitle'),
          },
        ]}
      />
      <Segment
        automationId="planner-scale"
        value={granularity}
        onChange={onGranularity}
        options={[
          { value: 'week', label: t('toolbar.ScaleWeek') },
          { value: 'month', label: t('toolbar.ScaleMonth') },
          { value: 'quarter', label: t('toolbar.ScaleQuarter') },
        ]}
      />
      <Segment
        automationId="planner-metric"
        value={metric}
        onChange={onMetric}
        options={[
          { value: 'pct', label: '%' },
          { value: 'hours', label: t('pmCommon:term.Hours') },
        ]}
      />

      <Box sx={{ flex: 1 }} />

      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <Typography variant="label" sx={{ color: 'var(--fg-3)' }}>
          {t('toolbar.LegendSpare')}
        </Typography>
        <Stack
          direction="row"
          sx={{
            height: '10px',
            borderRadius: 'var(--border-radius-small)',
            overflow: 'hidden',
            border: 'var(--border-width-thin) solid var(--border-subtle)',
          }}
        >
          {HEAT_LEGEND_STEPS.map((background) => (
            <Box key={background} sx={{ width: '22px', height: '100%', background }} />
          ))}
        </Stack>
        <Typography variant="label" sx={{ color: 'var(--fg-3)' }}>
          {t('toolbar.LegendOver')}
        </Typography>
      </Stack>

      {overCapacityCells > 0 && (
        <Chip
          size="small"
          color="coral"
          data-automation-id="planner-over-chip"
          icon={<MaterialSymbol name="warning" size={14} />}
          label={t('toolbar.OverCapacityCells', { count: overCapacityCells })}
        />
      )}
    </Stack>
  );
};
